import { useEffect, useState } from "react";
import api from "../api/axiosInstance";
import {
  ChevronLeft,
  ChevronRight,
  Wifi,
  Car,
  Coffee,
  Utensils,
  Tv,
  Wind,
  ShowerHead, // Changed from Shower to ShowerHead
  Bed,
  Users,
} from "lucide-react";

const GuestRoom = () => {
  const [rooms, setRooms] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch rooms from backend
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get("/guest-rooms/get/all");
        setRooms(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  // ✅ Improved amenity icon mapping
  const getAmenityIcon = (amenity) => {
    const amenityLower = amenity.toLowerCase().trim();

    if (amenityLower.includes("wifi") || amenityLower.includes("wi-fi")) {
      return <Wifi size={20} className="text-emerald-500" />;
    }
    if (amenityLower.includes("parking") || amenityLower.includes("car")) {
      return <Car size={20} className="text-emerald-500" />;
    }
    if (amenityLower.includes("coffee") || amenityLower.includes("tea")) {
      return <Coffee size={20} className="text-emerald-500" />;
    }
    if (amenityLower.includes("tv") || amenityLower.includes("television")) {
      return <Tv size={20} className="text-emerald-500" />;
    }
    if (
      amenityLower.includes("ac") ||
      amenityLower.includes("air conditioning") ||
      amenityLower.includes("air conditioner")
    ) {
      return <Wind size={20} className="text-emerald-500" />;
    }
    if (amenityLower.includes("bathroom") || amenityLower.includes("shower")) {
      return <ShowerHead size={20} className="text-emerald-500" />; // Fixed: ShowerHead instead of Shower
    }
    if (amenityLower.includes("bed")) {
      return <Bed size={20} className="text-emerald-500" />;
    }
    if (
      amenityLower.includes("restaurant") ||
      amenityLower.includes("food") ||
      amenityLower.includes("dining")
    ) {
      return <Utensils size={20} className="text-emerald-500" />;
    }
    if (
      amenityLower.includes("people") ||
      amenityLower.includes("guest") ||
      amenityLower.includes("capacity")
    ) {
      return <Users size={20} className="text-emerald-500" />;
    }

    // Default icon for unmatched amenities
    return (
      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
        <span className="text-white text-xs">✓</span>
      </div>
    );
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (rooms.length === 0)
    return (
      <div className="p-10 text-center text-gray-500">
        No guest rooms available
      </div>
    );

  const room = rooms[currentRoomIndex];
  const images = room.imageUrls || [];
  const amenities = room.amenities
    ? room.amenities
        .split(",")
        .map((a) => a.trim())
        .filter((a) => a)
    : [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Luxury Guest Rooms
          </h1>
          <p className="text-gray-600 mt-2">
            Experience comfort and elegance at RGCC
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src={images[currentImageIndex]}
                alt="Guest Room"
                className="w-full h-96 object-cover"
              />

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-all duration-200"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-all duration-200"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="grid grid-cols-3 gap-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      index === currentImageIndex
                        ? "border-emerald-500"
                        : "border-transparent"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Guest Room ${index + 1}`}
                      className="w-full h-24 object-cover hover:scale-105 transition-transform duration-200"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Room Details */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {room.title}
                  </h2>
                  <p className="text-gray-600 mt-2">{room.description}</p>
                </div>

                {/* {room.pricePerNight && (
                  <div className="text-right">
                    <p className="text-3xl font-bold text-emerald-600">
                      ৳{room.pricePerNight}
                    </p>
                    <p className="text-gray-500 text-sm">per night</p>
                  </div>
                )} */}
              </div>

              {/* Amenities */}
              {amenities.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Amenities
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {amenities.map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                      >
                        {getAmenityIcon(amenity)}
                        <span className="text-gray-700 font-medium">
                          {amenity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard Features */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Room Features
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    King-size bed with premium linens
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    En-suite bathroom with luxury toiletries
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Air conditioning and heating
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Flat-screen TV with satellite channels
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Work desk and seating area
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Daily housekeeping service
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Need Assistance?</h3>
          <p className="text-lg mb-6">
            Our team is here to help you with your booking
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-emerald-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200">
              Call: +88 01766693142
            </button>
            <button className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-emerald-600 transition-all duration-200">
              Email: rgcc66div@gmail.com
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestRoom;
